import React, { useState } from 'react';

import { Trans, useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { JumpIcon } from '@modernloop/shared/icons';
import { reportError } from '@modernloop/shared/observability';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import AtsName from 'src/entities/Ats/AtsName';

import { useOrgAtsService } from 'src/hooks/atsService';

import useCheckCreateCandidatePermission from './useCheckCreateCandidatePermission';

enum PermissionCheckStatus {
  Pending = 'Pending',
  Success = 'Success',
  Failed = 'Failed',
}

type Props = {
  jobId: string;
  onHasPermission: () => void;
  onClose: () => void;
  atsPermissionGrantUrl: string;
  permissionsListText: string;
  notAnAdminHelpText: string;
  isLoading?: boolean;
};

const BaseGreenhouseAshbyPermissionDialog = ({
  jobId,
  onHasPermission,
  onClose,
  atsPermissionGrantUrl,
  permissionsListText,
  notAnAdminHelpText,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const atsService = useOrgAtsService();
  const [loading, setLoading] = useState(false);
  const [permissionCheckStatus, setPermissionCheckStatus] = useState(PermissionCheckStatus.Pending);
  const [testCandidateAtsUrl, setTestCandidateAtsUrl] = useState<string | undefined>();

  const { enqueueSnackbar } = useSnackbar();

  const checkCreateCandidatePermission = useCheckCreateCandidatePermission(jobId);

  const handleCheckPermission = async () => {
    try {
      setLoading(true);
      const { hasPermission: permission, testCandidateAtsUrl: newTestCandidateAtsUrl } =
        await checkCreateCandidatePermission();
      setPermissionCheckStatus(permission ? PermissionCheckStatus.Success : PermissionCheckStatus.Failed);
      setTestCandidateAtsUrl(newTestCandidateAtsUrl || undefined);
    } catch (error) {
      enqueueSnackbar(t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.failedToCheckPermissions'), {
        variant: 'error',
      });
      reportError(error, { jobId });
    } finally {
      setLoading(false);
    }
  };

  const permissionsList = (
    <List sx={{ listStyleType: 'disc', margin: (theme) => theme.spacing(0, 2), padding: 0 }}>
      <ListItem sx={{ display: 'list-item' }}>{permissionsListText}</ListItem>
    </List>
  );

  return (
    <Dialog
      open
      maxWidth="xs"
      onClose={onClose}
      title={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.additionalPermissions', {
        atsName: atsService?.name,
      })}
      decoration={
        <img
          style={{ width: '64px', height: '64px' }}
          src="/static/images/scheduling/warning_64.png"
          alt={t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.changingCalendarWarningAlt')}
        />
      }
      cancelOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.cancel'),
        onClick: onClose,
      }}
      submitOptions={{
        label: t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.continue'),
        onClick: onHasPermission,
        isDisabled: permissionCheckStatus !== PermissionCheckStatus.Success,
      }}
    >
      <Stack spacing={1.5}>
        <Stack>
          <Typography variant="subtitle1">{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.step1')}</Typography>
          <Typography>{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.grantPermissions')}</Typography>
        </Stack>

        <Alert severity="warning">
          <AlertTitle>{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.candidates')}</AlertTitle>
          {permissionsList}
        </Alert>

        <Typography variant="body2" color="text.secondary">
          {notAnAdminHelpText}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          <Trans
            i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.needHelp"
            components={{
              learnMoreLink: <Link href={HelpCenterUrl.SOURCING_LINK_ATS_INTEGRATION} target="_blank" />,
            }}
          />
        </Typography>

        <Box>
          <Button
            variant="contained"
            color="primary"
            disabled={isLoading}
            href={atsPermissionGrantUrl}
            target="_blank"
            endIcon={<JumpIcon />}
          >
            <Trans
              i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.grantPermissionOnAtsName"
              components={{ atsName: <AtsName /> }}
            />
          </Button>
        </Box>

        <Divider flexItem />

        <Stack>
          <Typography variant="subtitle1">{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.step2')}</Typography>
          <Typography>{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.checkSetup')}</Typography>
        </Stack>

        {permissionCheckStatus === PermissionCheckStatus.Pending && (
          <Alert severity="info">
            <Typography variant="body2">
              <Trans
                i18nKey="hiring-dashboard-jobs:jobs.sourcingLinks.permissions.testCandidateInfoAtsName"
                components={{ atsName: <AtsName /> }}
              />
            </Typography>
            <br />
            <Typography variant="body2">
              {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.deleteTestCandidate')}
            </Typography>
          </Alert>
        )}

        {permissionCheckStatus === PermissionCheckStatus.Success && (
          <Alert
            severity="success"
            action={
              testCandidateAtsUrl ? (
                <Link
                  href={testCandidateAtsUrl}
                  target="_blank"
                  sx={{ flexShrink: 0, display: 'flex', gap: 0.5, mr: 3 }}
                  underline="hover"
                >
                  <JumpIcon color="success" />
                  <Typography color="success.dark" variant="body2">
                    {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.seeTestCandidate')}
                  </Typography>
                </Link>
              ) : undefined
            }
          >
            <AlertTitle>{t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.success')}</AlertTitle>
            <Typography variant="body2">
              {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.permissionsCorrect')}
            </Typography>
          </Alert>
        )}

        {permissionCheckStatus !== PermissionCheckStatus.Success && (
          <Box>
            <Button
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={24} /> : undefined}
              onClick={handleCheckPermission}
            >
              {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.checkPermissions')}
            </Button>
          </Box>
        )}

        {permissionCheckStatus === PermissionCheckStatus.Failed && (
          <Alert severity="error">
            <Typography variant="body2">
              {t('hiring-dashboard-jobs:jobs.sourcingLinks.permissions.couldNotFindPermissions')}
            </Typography>
            {permissionsList}
          </Alert>
        )}
      </Stack>
    </Dialog>
  );
};

export default BaseGreenhouseAshbyPermissionDialog;
